/**
 * sworn-doc-groups.ts — Wave 65 (บริการที่ 7: แปลและรับรองคำแปลสำหรับศาล/ราชการ)
 *
 * เนื้อหาเชิงลึกของ 6 กลุ่มเอกสารที่ต้องใช้ "คำแปลที่รับรองแล้ว" ในบริบทของ
 * ศาลยุติธรรม กระทรวงยุติธรรม กรมการกงสุล และหน่วยงานราชการไทย
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น (ห้ามเขียนข้อมูลที่อ้างอิงไม่ได้):
 *   - กรมการกงสุล กระทรวงการต่างประเทศ — งานนิติกรณ์เอกสารและการรับรองคำแปล
 *     (consular.mfa.go.th)
 *   - สำนักงานศาลยุติธรรม — ระเบียบว่าด้วยการใช้เอกสารภาษาต่างประเทศในคดี
 *     (coj.go.th) ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46
 *   - สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร
 *     (lawyerscouncil.or.th)
 *   - กรมการปกครอง กระทรวงมหาดไทย — เอกสารทะเบียนราษฎร/ทะเบียนครอบครัว
 *   - HCCH — สถานะอนุสัญญา Apostille ของไทย (src/data/apostille-status.ts)
 *
 * กติกา: ไม่ระบุราคา ไม่รับประกันระยะเวลา ไม่สร้างสถิติขึ้นเอง
 * และห้ามระบุว่าไทยออก Apostille ได้แล้วในวันนี้
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface SwornDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

const APOSTILLE_TH =
  `หมายเหตุสถานะกฎหมาย: ประเทศไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) ` +
  `เมื่อ ${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าว คำแปลและเอกสารยังต้องผ่าน ` +
  `การรับรองของกรมการกงสุลและสถานทูตปลายทางตามลำดับเดิม`;

const APOSTILLE_EN =
  `Legal status note: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}, and the Convention enters into force for Thailand on ` +
  `${APOSTILLE_STATUS.entryIntoForceEn} (source: HCCH). Until that date, translations and documents still pass ` +
  `through the Department of Consular Affairs and the destination embassy in the traditional order.`;

export const SWORN_DOC_GROUPS: SwornDocGroup[] = [
  {
    slug: "court-litigation",
    titleTh: "เอกสารคดีความและเอกสารที่ยื่นต่อศาล",
    titleEn: "Litigation documents filed with Thai courts",
    summaryTh:
      "คำฟ้อง คำให้การ สัญญาพิพาท พยานเอกสารภาษาต่างประเทศ คำพิพากษาจากศาลต่างประเทศ และเอกสารประกอบการบังคับคดี — กลุ่มที่คำแปลต้องแม่นยำระดับคำต่อคำเพราะถูกใช้เป็นพยานหลักฐาน",
    summaryEn:
      "Pleadings, defences, disputed contracts, foreign-language documentary evidence, foreign judgments and enforcement papers — the family where a translation is evidence, so accuracy is judged word by word.",
    bodyTh: [
      "ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 วางหลักว่าเอกสารที่ยื่นต่อศาลไทยต้องทำเป็นภาษาไทย หากเป็นเอกสารภาษาต่างประเทศ ศาลมีอำนาจสั่งให้คู่ความส่งคำแปลเป็นภาษาไทยที่มีผู้แปลรับรองความถูกต้องมาด้วย ด้วยเหตุนี้คำแปลในคดีความจึงไม่ใช่งานเอกสารประกอบทั่วไป แต่เป็นเอกสารที่คู่ความอีกฝ่ายและศาลจะตรวจสอบได้ตลอดกระบวนพิจารณา",
      "คำแปลในคดีต้องรักษาโครงสร้างของต้นฉบับให้ครบ ทั้งหัวเอกสาร เลขคดี วันที่ ลายมือชื่อ ตราประทับ เชิงอรรถ และข้อความที่ขีดฆ่า หากต้นฉบับมีส่วนที่อ่านไม่ออก ผู้แปลต้องระบุไว้ตรงตำแหน่งนั้นว่าอ่านไม่ได้ ไม่ใช่เดาเนื้อความ การเติมหรือตัดข้อความแม้เพียงเล็กน้อยอาจถูกอีกฝ่ายหยิบยกโต้แย้งน้ำหนักพยานได้",
      "ศัพท์กฎหมายเป็นจุดชี้ขาดของงานกลุ่มนี้ คำที่ดูใกล้เคียงกันในภาษาอังกฤษ เช่น termination กับ rescission หรือ indemnity กับ warranty ให้ผลทางกฎหมายต่างกันอย่างมีนัยสำคัญ ผู้แปลจึงต้องเลือกคำไทยที่สอดคล้องกับระบบกฎหมายไทย และควรคงคำต้นฉบับไว้ในวงเล็บเมื่อคำนั้นเป็นคำนิยามในสัญญา",
      "สำหรับคำพิพากษาหรือคำสั่งของศาลต่างประเทศที่จะนำมาใช้ในไทย นอกจากคำแปลแล้ว ตัวเอกสารมักต้องผ่านการรับรองจากหน่วยงานของประเทศต้นทางและสถานเอกอัครราชทูตไทยในประเทศนั้นก่อน เงื่อนไขต่างกันตามประเภทคดีและประเทศต้นทาง จึงควรตรวจสอบกับศาลหรือหน่วยงานที่รับเรื่องก่อนเริ่มแปล",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Section 46 of the Thai Civil Procedure Code requires documents submitted to Thai courts to be in Thai; where a document is in a foreign language, the court may order the party to file a Thai translation certified as accurate by the translator. A litigation translation is therefore not a supporting document — it is evidence that the opposing party and the bench can scrutinise throughout the proceedings.",
      "The translation must preserve the full structure of the original: letterhead, case number, dates, signatures, seals, footnotes and struck-through text. Where the original is illegible, the translator marks the exact position as illegible rather than guessing. Adding or omitting even a short phrase gives the other side grounds to challenge the evidential weight of the document.",
      "Legal terminology decides the quality of this work. Terms that look interchangeable in English — termination and rescission, indemnity and warranty — carry materially different consequences. The translator must choose Thai wording consistent with Thai legal concepts and retain the source term in brackets whenever it is a defined term in the contract.",
      "For a foreign judgment or court order to be used in Thailand, the document itself usually needs certification in the country of origin and by the Royal Thai Embassy there, in addition to the translation. Requirements vary by case type and country, so confirm with the receiving court or authority before translation begins.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "คำฟ้อง คำให้การ คำร้อง คำแถลง",
      "สัญญาที่เป็นประเด็นข้อพิพาท และเอกสารแนบท้ายสัญญา",
      "พยานเอกสารภาษาต่างประเทศ อีเมล และหลักฐานทางธุรกรรม",
      "คำพิพากษา คำสั่งศาล และหมายบังคับคดีจากต่างประเทศ",
      "คำแปลหนังสือมอบอำนาจสำหรับดำเนินคดี",
      "รายงานผู้เชี่ยวชาญและความเห็นทางกฎหมาย",
    ],
    documentsEn: [
      "Pleadings, defences, motions and submissions",
      "Contracts in dispute and their annexes",
      "Foreign-language documentary evidence, emails and transaction records",
      "Foreign judgments, court orders and enforcement writs",
      "Powers of attorney for litigation",
      "Expert reports and legal opinions",
    ],
    chainTh: [
      "รวบรวมต้นฉบับให้ครบทุกหน้า รวมหน้าที่มีเพียงตราประทับหรือลายมือชื่อ",
      "แปลเป็นภาษาไทย (หรือภาษาอังกฤษ กรณีส่งออกนอกประเทศ) โดยคงรูปแบบต้นฉบับ",
      "ผู้แปลลงนามรับรองความถูกต้องของคำแปลแนบท้าย",
      "หากศาลหรือคู่ความต้องการ ให้ทนายความผู้ทำคำรับรองลายมือชื่อรับรองเพิ่ม",
      "ยื่นต่อศาลพร้อมต้นฉบับเพื่อให้ตรวจเทียบได้",
    ],
    chainEn: [
      "Collect every page of the original, including pages carrying only a seal or signature",
      "Translate into Thai (or English for outbound use) preserving the original layout",
      "Translator signs a certificate of accuracy attached to the translation",
      "Where the court or the other party requires it, add certification by a Notarial Services Attorney",
      "File with the court together with the originals so they can be compared",
    ],
    checklistTh: [
      "ตรวจว่าเลขคดี ชื่อคู่ความ และวันที่ในคำแปลตรงกับต้นฉบับทุกตัวอักษร",
      "ทำอภิธานศัพท์คู่ความ/คำนิยามในสัญญา เพื่อให้คำแปลสม่ำเสมอทั้งสำนวน",
      "ระบุจำนวนหน้าไว้ในคำรับรอง เพื่อกันการเพิ่มหรือถอดหน้าในภายหลัง",
      "อย่าย่อความหรือแปลเฉพาะบางย่อหน้า เว้นแต่ศาลอนุญาตให้แปลเฉพาะส่วน",
      "เก็บไฟล์ต้นฉบับสแกนความละเอียดสูงไว้อ้างอิงหากถูกโต้แย้ง",
    ],
    checklistEn: [
      "Verify case number, party names and dates match the original exactly",
      "Build a glossary of party names and defined terms so the whole file stays consistent",
      "State the page count in the certificate to prevent later insertion or removal of pages",
      "Do not summarise or translate selected paragraphs unless the court allows partial translation",
      "Keep a high-resolution scan of the original in case the translation is challenged",
    ],
    faqsTh: [
      {
        q: "ศาลไทยบังคับให้เอกสารภาษาต่างประเทศต้องมีคำแปลหรือไม่",
        a: "ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 กำหนดให้กระบวนพิจารณาใช้ภาษาไทย และศาลมีอำนาจสั่งให้คู่ความส่งคำแปลภาษาไทยที่มีผู้แปลรับรองความถูกต้องพร้อมเอกสารภาษาต่างประเทศ ในทางปฏิบัติจึงควรเตรียมคำแปลไปพร้อมกันตั้งแต่วันยื่น",
      },
      {
        q: "คำแปลในคดีต้องให้ทนายความรับรองด้วยหรือไม่",
        a: "หลักคือผู้แปลเป็นผู้รับรองความถูกต้องของคำแปล ส่วนการให้ทนายความผู้ทำคำรับรองลายมือชื่อ (Notarial Services Attorney) รับรองเพิ่ม จะใช้เมื่อหน่วยงานปลายทางหรือคู่ความต้องการหลักฐานยืนยันตัวผู้ลงนาม ควรสอบถามผู้รับเอกสารก่อนว่าต้องการระดับใด",
      },
      {
        q: "ถ้าเอกสารมีบางส่วนอ่านไม่ออกต้องทำอย่างไร",
        a: "ผู้แปลต้องระบุไว้ตรงตำแหน่งนั้นว่าอ่านไม่ได้ (illegible) ห้ามเดาหรือเติมข้อความ เพราะการเติมเนื้อความที่ไม่ปรากฏในต้นฉบับทำให้คำแปลไม่ตรงกับพยานเอกสารและอาจถูกโต้แย้งได้",
      },
      {
        q: "คำพิพากษาจากศาลต่างประเทศนำมาใช้ในไทยได้ทันทีหรือไม่",
        a: "การนำผลของคำพิพากษาต่างประเทศมาใช้ในไทยขึ้นอยู่กับประเภทคดีและต้องผ่านกระบวนการทางศาลไทย เอกสารมักต้องได้รับการรับรองจากหน่วยงานประเทศต้นทางและสถานเอกอัครราชทูตไทยก่อน จึงควรปรึกษาทนายความและตรวจสอบกับศาลที่รับเรื่องก่อนดำเนินการ",
      },
      {
        q: "ต้องแปลทั้งฉบับหรือแปลเฉพาะส่วนที่เกี่ยวข้อง",
        a: "โดยหลักควรแปลทั้งฉบับเพื่อไม่ให้ถูกโต้แย้งว่าเลือกแปลเฉพาะส่วนที่เป็นประโยชน์ต่อฝ่ายตน หากศาลอนุญาตให้แปลเฉพาะส่วน ต้องระบุขอบเขตที่แปลไว้ในคำรับรองอย่างชัดเจน",
      },
      {
        q: "ชื่อบุคคลและชื่อบริษัทควรทับศัพท์อย่างไร",
        a: "ควรยึดการสะกดตามเอกสารราชการที่ใช้อยู่จริง เช่น หนังสือเดินทางหรือหนังสือรับรองนิติบุคคล และคงชื่อภาษาต้นฉบับไว้ในวงเล็บ เพื่อให้ศาลเชื่อมโยงกับเอกสารอื่นในสำนวนได้โดยไม่คลาดเคลื่อน",
      },
    ],
    faqsEn: [
      {
        q: "Do Thai courts require translations of foreign-language documents?",
        a: "Section 46 of the Civil Procedure Code provides that proceedings are conducted in Thai and allows the court to order a party to file a Thai translation certified as accurate by the translator. In practice, file the translation together with the foreign-language document from the outset.",
      },
      {
        q: "Must a lawyer certify a litigation translation?",
        a: "As a rule the translator certifies the accuracy of the translation. Additional certification by a Notarial Services Attorney is used when the receiving authority or the opposing party wants confirmation of the signatory's identity. Ask the recipient which level is required.",
      },
      {
        q: "What if part of the document is illegible?",
        a: "The translator marks that exact position as illegible. Guessing or filling in text is not permitted, because content that does not appear in the original makes the translation diverge from the evidence and opens it to challenge.",
      },
      {
        q: "Can a foreign judgment be used in Thailand immediately?",
        a: "Giving effect to a foreign judgment in Thailand depends on the type of case and requires a Thai court process. The documents normally need certification in the country of origin and by the Royal Thai Embassy there. Consult a lawyer and confirm with the receiving court first.",
      },
      {
        q: "Translate the whole document or only the relevant parts?",
        a: "Translate in full wherever possible, so the other side cannot argue you selected only favourable passages. If the court permits partial translation, state the exact scope in the certificate of accuracy.",
      },
      {
        q: "How should personal and company names be transliterated?",
        a: "Follow the spelling on the official document already in use — passport or company affidavit — and keep the source-language name in brackets so the court can match it against other documents in the file.",
      },
    ],
  },
  {
    slug: "civil-status",
    titleTh: "เอกสารทะเบียนราษฎรและสถานะบุคคล",
    titleEn: "Civil status and registration documents",
    summaryTh:
      "สูติบัตร ทะเบียนบ้าน ทะเบียนสมรส ทะเบียนหย่า หนังสือรับรองความเป็นโสด และใบเปลี่ยนชื่อ — คำแปลกลุ่มนี้ถูกตรวจเทียบกับต้นฉบับทีละบรรทัดที่กรมการกงสุล",
    summaryEn:
      "Birth certificates, house registration, marriage and divorce records, single-status affidavits and name-change certificates — translations compared line by line at the Department of Consular Affairs.",
    bodyTh: [
      "เอกสารสถานะบุคคลออกโดยสำนักทะเบียนอำเภอหรือสำนักงานเขตภายใต้กรมการปกครอง กระทรวงมหาดไทย เมื่อจะใช้ต่างประเทศต้องแปลเป็นภาษาอังกฤษแล้วนำไปรับรองนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ การรับรองนี้เป็นการรับรองความถูกต้องของคำแปลเทียบกับต้นฉบับ ไม่ใช่การรับรองข้อเท็จจริงที่ปรากฏในเอกสาร",
      "จุดตกบ่อยที่สุดคือการสะกดชื่อไม่ตรงกับหนังสือเดินทาง คำนำหน้าชื่อไม่ตรงกับสถานะปัจจุบัน และการแปลชื่อสถานที่ราชการไม่ตรงกับชื่อทางการ เช่น ชื่ออำเภอ เขต หรือจังหวัดที่มีการเปลี่ยนชื่อในภายหลัง ควรเตรียมสำเนาหนังสือเดินทางของทุกคนที่มีชื่อในเอกสารไปด้วยเพื่อให้คำแปลอิงการสะกดเดียวกัน",
      "รูปแบบวันที่เป็นอีกเรื่องที่ต้องระวัง เอกสารไทยใช้พุทธศักราช เมื่อแปลเป็นภาษาอังกฤษต้องแปลงเป็นคริสต์ศักราชให้ถูกต้อง และควรคงเลขปี พ.ศ. ไว้ในวงเล็บเพื่อให้ตรวจเทียบกับต้นฉบับได้ ส่วนเลขประจำตัวประชาชนและเลขที่เอกสารต้องคัดลอกตรงตัวโดยไม่ตัดเลขศูนย์นำหน้า",
      "หลายหน่วยงานปลายทางกำหนดให้ใช้เอกสารที่คัดรับรองใหม่ภายในระยะเวลาที่กำหนด และบางแห่งไม่รับสำเนาที่ถ่ายเอกสารเอง จึงควรตรวจสอบเงื่อนไขของผู้รับเอกสารก่อนเริ่มแปล เพราะเงื่อนไขนี้ต่างกันในแต่ละประเทศและเปลี่ยนแปลงได้",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Civil status records are issued by district registration offices under the Department of Provincial Administration. To be used abroad they must be translated into English and legalised by the Legalisation Division of the Department of Consular Affairs. That endorsement certifies the translation against the original — it does not certify the facts recorded in the document.",
      "The most common failures are a spelling that differs from the passport, a title that no longer matches the person's current status, and official place names translated inconsistently with their formal English form, including districts renamed after the record was issued. Bring passport copies for everyone named in the document so the translation follows one spelling.",
      "Dates need equal care. Thai records use the Buddhist Era; the English translation must convert to the Common Era correctly and should keep the B.E. year in brackets so the two can be compared. National ID numbers and document numbers are copied exactly, including leading zeros.",
      "Many receiving authorities require a recently issued certified extract and refuse self-made photocopies. Confirm the recipient's rule before translation, since it differs by country and changes over time.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "สูติบัตรและหนังสือรับรองการเกิด",
      "ทะเบียนบ้าน (ท.ร.14) และแบบรับรองรายการทะเบียนราษฎร",
      "ทะเบียนสมรส (คร.2) ใบสำคัญการสมรส (คร.3)",
      "ทะเบียนหย่า (คร.6) ใบสำคัญการหย่า (คร.7)",
      "หนังสือรับรองความเป็นโสด / สถานภาพการสมรส",
      "ใบเปลี่ยนชื่อตัว (ช.3) และเปลี่ยนชื่อสกุล (ช.2)",
      "มรณบัตรและหนังสือรับรองการตาย",
    ],
    documentsEn: [
      "Birth certificates and birth notifications",
      "House registration (Tor Ror 14) and certified registration extracts",
      "Marriage registration (Kor Ror 2) and marriage certificate (Kor Ror 3)",
      "Divorce registration (Kor Ror 6) and divorce certificate (Kor Ror 7)",
      "Single-status / marital-status affidavits",
      "Given-name and family-name change certificates",
      "Death certificates and death notifications",
    ],
    chainTh: [
      "คัดเอกสารฉบับรับรองจากสำนักทะเบียนที่ออกเอกสาร",
      "แปลเป็นภาษาอังกฤษตามรูปแบบที่กรมการกงสุลใช้ตรวจ",
      "ยื่นรับรองนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "หากประเทศปลายทางกำหนด ให้นำไปรับรองต่อที่สถานทูตของประเทศนั้นในไทย",
    ],
    chainEn: [
      "Obtain a certified extract from the issuing registration office",
      "Translate into English in the format consular officers verify against",
      "Submit for legalisation at the Legalisation Division, Department of Consular Affairs",
      "If the destination requires it, obtain further legalisation at that country's embassy in Thailand",
    ],
    checklistTh: [
      "เตรียมสำเนาหนังสือเดินทางของทุกคนที่มีชื่อในเอกสาร",
      "ตรวจการแปลง พ.ศ. เป็น ค.ศ. ทุกวันที่ในเอกสาร",
      "ตรวจชื่ออำเภอ/เขต/จังหวัด ให้ตรงกับชื่อทางการปัจจุบัน",
      "คัดลอกเลขประจำตัวประชาชนและเลขที่เอกสารตรงตัว",
      "ถามหน่วยงานปลายทางว่ารับเอกสารที่ออกย้อนหลังได้กี่เดือน",
    ],
    checklistEn: [
      "Prepare passport copies for everyone named in the document",
      "Check every Buddhist-Era to Common-Era date conversion",
      "Match district and province names to their current official forms",
      "Copy ID and document numbers character for character",
      "Ask the receiving authority how recent the extract must be",
    ],
    faqsTh: [
      {
        q: "กรมการกงสุลรับรองอะไรในคำแปล",
        a: "กองสัญชาติและนิติกรณ์ กรมการกงสุล รับรองว่าคำแปลถูกต้องตรงกับเอกสารต้นฉบับที่ยื่นมา ไม่ได้รับรองว่าข้อเท็จจริงในเอกสารเป็นความจริง และไม่ได้รับรองคุณสมบัติของผู้ถือเอกสาร",
      },
      {
        q: "แปลเองแล้วยื่นรับรองได้หรือไม่",
        a: "ในทางปฏิบัติ เอกสารต้องผ่านการตรวจเทียบคำแปลกับต้นฉบับ หากคำแปลไม่ตรงตามรูปแบบและถ้อยคำที่ถูกต้อง เจ้าหน้าที่จะให้แก้ไขและยื่นใหม่ ควรตรวจเงื่อนไขล่าสุดกับกรมการกงสุลก่อนยื่น เพราะแนวปฏิบัติปรับปรุงได้",
      },
      {
        q: "ชื่อในสูติบัตรสะกดไม่ตรงกับหนังสือเดินทางต้องทำอย่างไร",
        a: "ควรยึดการสะกดตามหนังสือเดินทางเป็นหลักและอธิบายความต่างไว้ หากความต่างมีนัยสำคัญ อาจต้องขอหนังสือรับรองว่าเป็นบุคคลเดียวกันจากสำนักทะเบียน เพื่อให้หน่วยงานปลายทางเชื่อมโยงเอกสารได้",
      },
      {
        q: "หนังสือรับรองความเป็นโสดมีอายุใช้งานเท่าใด",
        a: "อายุการยอมรับกำหนดโดยหน่วยงานปลายทางหรือสถานทูตของประเทศที่จะใช้ ไม่ใช่โดยผู้แปล จึงต้องสอบถามผู้รับเอกสารโดยตรงก่อนเริ่มกระบวนการ",
      },
      {
        q: "เอกสารทะเบียนราษฎรต้องให้ทนายรับรองด้วยหรือไม่",
        a: "เอกสารราชการที่ออกโดยหน่วยงานรัฐยื่นรับรองนิติกรณ์ได้โดยตรง ส่วนการรับรองโดยทนายความผู้ทำคำรับรองลายมือชื่อใช้กับเอกสารที่เอกชนจัดทำและลงนามเอง เช่น หนังสือรับรองตนเองหรือหนังสือมอบอำนาจ",
      },
      {
        q: "อยู่ต่างประเทศจะดำเนินการอย่างไร",
        a: "เอกสารทะเบียนราษฎรไทยต้องคัดจากสำนักทะเบียนในประเทศไทย จึงมักมอบอำนาจให้ผู้แทนดำเนินการแทน โดยหนังสือมอบอำนาจที่ทำในต่างประเทศต้องผ่านการรับรองจากสถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศนั้นก่อน",
      },
    ],
    faqsEn: [
      {
        q: "What exactly does the Department of Consular Affairs certify?",
        a: "The Legalisation Division certifies that the translation corresponds to the original document submitted. It does not certify that the facts in the document are true, nor the holder's qualifications.",
      },
      {
        q: "Can I translate the document myself and submit it?",
        a: "In practice the translation is verified line by line against the original; if the wording or format is wrong, officers return it for correction and resubmission. Confirm the current requirements with the Department of Consular Affairs before filing, as practice is updated from time to time.",
      },
      {
        q: "My birth certificate spelling differs from my passport — what now?",
        a: "Follow the passport spelling and note the discrepancy. Where the difference is material, obtain a same-person certificate from the registration office so the receiving authority can link the records.",
      },
      {
        q: "How long is a single-status affidavit valid?",
        a: "Validity is set by the receiving authority or embassy in the destination country, not by the translator. Ask the recipient directly before starting.",
      },
      {
        q: "Do registration records need a lawyer's certification too?",
        a: "Government-issued records can be submitted for legalisation directly. Certification by a Notarial Services Attorney applies to privately prepared and signed documents such as self-declarations and powers of attorney.",
      },
      {
        q: "How do I arrange this from abroad?",
        a: "Thai registration records must be obtained inside Thailand, usually by a representative under a power of attorney. A power of attorney executed abroad must first be certified by the Royal Thai Embassy or Consulate-General in that country.",
      },
    ],
  },
  {
    slug: "education-credentials",
    titleTh: "เอกสารการศึกษาและคุณวุฒิ",
    titleEn: "Education and qualification documents",
    summaryTh:
      "ปริญญาบัตร ทรานสคริปต์ ใบรับรองการศึกษา ใบประกอบวิชาชีพ และหนังสือรับรองหลักสูตร — ใช้สมัครเรียน สมัครงาน ขอใบอนุญาตประกอบวิชาชีพ และยื่นวีซ่า",
    summaryEn:
      "Degree certificates, transcripts, school certificates, professional licences and course descriptions — used for admissions, employment, professional registration and visa filings.",
    bodyTh: [
      "เอกสารการศึกษาของไทยออกโดยสถาบันการศึกษา ซึ่งอาจเป็นหน่วยงานรัฐหรือเอกชน ความแตกต่างนี้มีผลต่อขั้นตอน สถาบันของรัฐมักออกเอกสารที่มีตราประทับและลายมือชื่อผู้มีอำนาจซึ่งกรมการกงสุลตรวจสอบได้ ส่วนสถาบันเอกชนบางแห่งอาจต้องมีหนังสือรับรองจากหน่วยงานกำกับดูแลประกอบ ควรสอบถามสถาบันผู้ออกเอกสารก่อนว่าต้องขอเอกสารรูปแบบใด",
      "ทรานสคริปต์เป็นเอกสารที่แปลผิดพลาดง่ายที่สุดในกลุ่มนี้ เพราะมีทั้งรหัสวิชา ชื่อวิชา หน่วยกิต ระบบเกรด และเกณฑ์การสำเร็จการศึกษา ผู้แปลต้องไม่แปลงระบบเกรดไทยเป็นระบบของประเทศปลายทางเอง แต่ต้องคงค่าตามต้นฉบับและคงคำอธิบายระบบเกรดที่พิมพ์อยู่บนเอกสารไว้ทั้งหมด การเทียบวุฒิเป็นอำนาจของหน่วยงานปลายทาง ไม่ใช่ของผู้แปล",
      "ชื่อปริญญาและชื่อสาขาต้องใช้ชื่อภาษาอังกฤษทางการที่สถาบันกำหนด หากสถาบันมีชื่อภาษาอังกฤษที่จดไว้อยู่แล้ว ต้องใช้ชื่อนั้น ไม่แปลใหม่ตามความเข้าใจของผู้แปล เพราะหน่วยงานรับรองคุณวุฒิในต่างประเทศจะตรวจเทียบกับฐานข้อมูลชื่อสถาบัน",
      "สำหรับใบประกอบวิชาชีพ เช่น สาขาสุขภาพหรือวิศวกรรม เอกสารมักต้องมาพร้อมหนังสือรับรองสถานะใบอนุญาตที่ออกโดยสภาวิชาชีพที่กำกับดูแล และบางประเทศกำหนดให้สภาวิชาชีพส่งเอกสารตรงถึงหน่วยงานผู้รับ ควรตรวจข้อกำหนดนี้ก่อน เพราะหากส่งเองอาจไม่ได้รับการยอมรับ",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Thai education documents are issued by public or private institutions, and that distinction affects the process. Public institutions issue documents with seals and authorised signatures that consular officers can verify; some private institutions also need a supporting certificate from their regulator. Ask the issuing institution which format to request before you start.",
      "Transcripts are the easiest document in this family to get wrong: course codes, course titles, credits, the grading scale and graduation criteria all matter. The translator must not convert Thai grades into the destination country's scale; values are kept as printed, together with the full grading-scale legend. Credential equivalence is decided by the receiving authority, not by the translator.",
      "Degree and field names must use the institution's official English titles. Where an institution already has a registered English name, that name is used rather than a fresh translation, because foreign credential evaluators check it against institution databases.",
      "Professional licences — in health or engineering, for example — usually need an accompanying status certificate from the regulating professional council, and some countries require the council to send documents directly to the receiving body. Check this first: self-sent documents may not be accepted.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "ปริญญาบัตร ประกาศนียบัตร และใบแสดงวุฒิ",
      "ทรานสคริปต์ / ใบแสดงผลการศึกษา",
      "หนังสือรับรองสถานภาพนักศึกษาและหนังสือรับรองการสำเร็จการศึกษา",
      "คำอธิบายรายวิชาและโครงสร้างหลักสูตร",
      "ใบอนุญาตประกอบวิชาชีพและหนังสือรับรองสถานะใบอนุญาต",
      "ใบรับรองการอบรมและใบประกาศนียบัตรวิชาชีพ",
    ],
    documentsEn: [
      "Degree certificates, diplomas and qualification certificates",
      "Academic transcripts",
      "Enrolment and graduation verification letters",
      "Course descriptions and curriculum structures",
      "Professional licences and licence status certificates",
      "Training records and vocational certificates",
    ],
    chainTh: [
      "ขอเอกสารฉบับทางการจากสถาบัน พร้อมตราประทับและลายมือชื่อผู้มีอำนาจ",
      "แปลเป็นภาษาอังกฤษโดยคงชื่อทางการของสถาบันและระบบเกรดเดิม",
      "ยื่นรับรองนิติกรณ์ที่กรมการกงสุล",
      "รับรองต่อที่สถานทูตประเทศปลายทาง หากหน่วยงานผู้รับกำหนด",
    ],
    chainEn: [
      "Request official copies from the institution with seal and authorised signature",
      "Translate into English keeping official institution names and the original grading scale",
      "Legalise at the Department of Consular Affairs",
      "Add embassy legalisation if the receiving body requires it",
    ],
    checklistTh: [
      "ใช้ชื่อภาษาอังกฤษทางการของสถาบันและชื่อปริญญา",
      "คงคำอธิบายระบบเกรดและเกณฑ์สำเร็จการศึกษาไว้ครบ",
      "อย่าแปลงเกรดหรือคำนวณ GPA ใหม่ในคำแปล",
      "ตรวจว่าทรานสคริปต์ครบทุกหน้าและมีตราประทับต่อเนื่อง",
      "ถามหน่วยงานปลายทางว่าต้องให้สถาบันส่งเอกสารโดยตรงหรือไม่",
    ],
    checklistEn: [
      "Use the institution's and degree's official English titles",
      "Keep the grading legend and graduation criteria in full",
      "Never convert grades or recalculate GPA in the translation",
      "Check the transcript is complete and the seals run across pages",
      "Ask whether the receiving body requires direct institutional delivery",
    ],
    faqsTh: [
      {
        q: "ผู้แปลเทียบวุฒิการศึกษาให้ได้หรือไม่",
        a: "ไม่ได้ การเทียบวุฒิและการตัดสินว่าคุณวุฒิเทียบเท่าระดับใดเป็นอำนาจของหน่วยงานรับรองคุณวุฒิหรือหน่วยงานปลายทาง ผู้แปลมีหน้าที่แปลข้อความให้ตรงต้นฉบับเท่านั้น",
      },
      {
        q: "ทรานสคริปต์ต้องแปลทุกหน้าหรือไม่",
        a: "ควรแปลครบทุกหน้ารวมถึงด้านหลังที่พิมพ์คำอธิบายระบบเกรด เพราะหน่วยงานปลายทางใช้คำอธิบายนั้นในการอ่านผลการเรียน หากขาดไปมักถูกขอเอกสารเพิ่ม",
      },
      {
        q: "ปริญญาบัตรภาษาอังกฤษที่สถาบันออกให้ ยังต้องแปลอีกหรือไม่",
        a: "หากสถาบันออกฉบับภาษาอังกฤษอย่างเป็นทางการอยู่แล้ว โดยทั่วไปไม่ต้องแปลซ้ำ แต่ยังอาจต้องนำไปรับรองนิติกรณ์และรับรองสถานทูตตามข้อกำหนดของผู้รับเอกสาร",
      },
      {
        q: "ใบประกอบวิชาชีพต้องมีเอกสารประกอบอะไรบ้าง",
        a: "โดยทั่วไปต้องมีหนังสือรับรองสถานะใบอนุญาตที่ออกโดยสภาวิชาชีพผู้กำกับดูแล ระบุว่าใบอนุญาตยังมีผลอยู่ ณ วันที่ออกหนังสือ ข้อกำหนดรายละเอียดต่างกันตามประเทศและวิชาชีพ",
      },
      {
        q: "เอกสารการศึกษาจากโรงเรียนนานาชาติในไทยทำอย่างไร",
        a: "ตรวจสอบก่อนว่าโรงเรียนอยู่ในกำกับของหน่วยงานใด และเอกสารมีลายมือชื่อผู้มีอำนาจที่ตรวจสอบได้หรือไม่ บางกรณีต้องมีหนังสือรับรองจากหน่วยงานกำกับดูแลประกอบการยื่นนิติกรณ์",
      },
      {
        q: "ชื่อสาขาที่ไม่มีคำแปลตรงตัวควรทำอย่างไร",
        a: "ใช้ชื่อภาษาอังกฤษที่สถาบันกำหนดเป็นหลัก หากไม่มี ให้แปลตามความหมายและคงชื่อภาษาไทยไว้ในวงเล็บ เพื่อให้ผู้ตรวจสอบเทียบกับเอกสารต้นฉบับได้",
      },
    ],
    faqsEn: [
      {
        q: "Can the translator assess credential equivalence?",
        a: "No. Deciding what a qualification is equivalent to is the role of a credential evaluation body or the receiving authority. The translator only renders the text faithfully.",
      },
      {
        q: "Does every transcript page need translating?",
        a: "Yes, including the reverse side carrying the grading legend, which the receiving body uses to read the results. Missing it usually triggers a request for more documents.",
      },
      {
        q: "My degree was issued in English — does it still need translation?",
        a: "If the institution issues an official English version, a translation is generally unnecessary, but legalisation and embassy certification may still be required by the recipient.",
      },
      {
        q: "What supports a professional licence?",
        a: "Normally a licence status certificate from the regulating council confirming the licence is current as at its issue date. Detailed requirements vary by country and profession.",
      },
      {
        q: "What about records from an international school in Thailand?",
        a: "Check which authority oversees the school and whether the documents carry verifiable authorised signatures. Some cases need a supporting certificate from the regulator before legalisation.",
      },
      {
        q: "How are field names with no direct English equivalent handled?",
        a: "Use the institution's official English name; if none exists, translate by meaning and keep the Thai name in brackets so reviewers can match it to the original.",
      },
    ],
  },
  {
    slug: "corporate-commercial",
    titleTh: "เอกสารนิติบุคคลและการค้า",
    titleEn: "Corporate and commercial documents",
    summaryTh:
      "หนังสือรับรองบริษัท บอจ.5 หนังสือบริคณห์สนธิ งบการเงิน มติที่ประชุม และสัญญาทางธุรกิจ — ใช้ยื่นคู่ค้าต่างประเทศ ธนาคาร และการประมูลงาน",
    summaryEn:
      "Company affidavits, shareholder lists, memoranda of association, financial statements, board resolutions and commercial contracts — for foreign counterparties, banks and tenders.",
    bodyTh: [
      "หนังสือรับรองนิติบุคคลและเอกสารทะเบียนธุรกิจออกโดยกรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์ เอกสารเหล่านี้มีอายุการยอมรับสั้นในทางปฏิบัติ เพราะสะท้อนสถานะ ณ วันที่คัด คู่ค้าและธนาคารต่างประเทศส่วนใหญ่จึงกำหนดให้คัดใหม่ก่อนยื่น ควรตรวจข้อกำหนดของผู้รับก่อนเริ่มแปลเพื่อไม่ให้เอกสารหมดอายุระหว่างขั้นตอน",
      "คำแปลเอกสารนิติบุคคลต้องคงชื่อบริษัทภาษาอังกฤษที่จดทะเบียนไว้อย่างเคร่งครัด รวมถึงคำต่อท้ายรูปแบบนิติบุคคล หากบริษัทจดชื่อภาษาอังกฤษไว้แล้ว ห้ามแปลชื่อใหม่ เพราะธนาคารและนายทะเบียนต่างประเทศจะเทียบชื่อกับเอกสารจดทะเบียนอื่น เช่น ใบทะเบียนภาษีมูลค่าเพิ่มหรือสัญญาที่ลงนามไว้",
      "วัตถุประสงค์ของบริษัทและข้อจำกัดอำนาจกรรมการเป็นส่วนที่มีผลทางกฎหมายสูงสุดในหนังสือรับรอง ต้องแปลให้ครบทุกข้อ โดยเฉพาะเงื่อนไขการลงนามผูกพัน เช่น จำนวนกรรมการที่ต้องลงนามร่วมกันและการประทับตราสำคัญ หากคำแปลตกหล่นข้อจำกัดเหล่านี้ คู่สัญญาต่างประเทศอาจเข้าใจอำนาจลงนามคลาดเคลื่อน",
      "งบการเงินมีศัพท์บัญชีเฉพาะที่ต้องใช้ให้ตรงกับมาตรฐานการรายงานทางการเงินที่ใช้จัดทำงบนั้น และต้องคงหมายเหตุประกอบงบการเงินไว้ เพราะหมายเหตุคือส่วนที่อธิบายนโยบายบัญชีและภาระผูกพัน การแปลเฉพาะหน้างบดุลโดยตัดหมายเหตุออกมักถูกผู้รับตีกลับ",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Company affidavits and business registry records are issued by the Department of Business Development, Ministry of Commerce. In practice they have a short acceptance window because they show status as at the extraction date, so most foreign counterparties and banks require a fresh extract. Check the recipient's rule before translation so the document does not expire mid-process.",
      "Corporate translations must keep the registered English company name exactly, including the entity suffix. Where an English name is already registered, it is never re-translated: banks and foreign registrars match it against other records such as VAT registration or signed contracts.",
      "The objectives clause and the limits on directors' authority carry the greatest legal weight in an affidavit and must be translated in full — particularly binding-signature conditions such as how many directors must sign jointly and whether the company seal is required. Omitting these can leave a foreign counterparty with the wrong view of signing authority.",
      "Financial statements use accounting terminology that must match the reporting framework under which they were prepared, and the notes must be included: they explain accounting policies and obligations. Translating only the balance sheet and dropping the notes is a common cause of rejection.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "หนังสือรับรองนิติบุคคล (กรมพัฒนาธุรกิจการค้า)",
      "บัญชีรายชื่อผู้ถือหุ้น (บอจ.5)",
      "หนังสือบริคณห์สนธิและข้อบังคับบริษัท",
      "งบการเงินและหมายเหตุประกอบงบการเงิน",
      "รายงานการประชุมและมติคณะกรรมการ/ผู้ถือหุ้น",
      "สัญญาทางธุรกิจ ใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20) และใบอนุญาตประกอบกิจการ",
    ],
    documentsEn: [
      "Company affidavit issued by the Department of Business Development",
      "Shareholder list (Bor Or Jor 5)",
      "Memorandum and articles of association",
      "Financial statements with notes",
      "Minutes and board or shareholder resolutions",
      "Commercial contracts, VAT registration (Por Por 20) and operating licences",
    ],
    chainTh: [
      "คัดหนังสือรับรองฉบับใหม่จากกรมพัฒนาธุรกิจการค้า",
      "แปลโดยคงชื่อภาษาอังกฤษที่จดทะเบียนและข้อจำกัดอำนาจลงนามครบถ้วน",
      "เอกสารที่บริษัทจัดทำเอง ให้ผู้มีอำนาจลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อ",
      "ยื่นรับรองนิติกรณ์ที่กรมการกงสุล",
      "รับรองต่อที่สถานทูตประเทศปลายทางตามที่ผู้รับกำหนด",
    ],
    chainEn: [
      "Obtain a fresh affidavit from the Department of Business Development",
      "Translate keeping the registered English name and all signing-authority limits",
      "For company-prepared documents, have the authorised signatory sign before a Notarial Services Attorney",
      "Legalise at the Department of Consular Affairs",
      "Add embassy legalisation where the recipient requires it",
    ],
    checklistTh: [
      "ใช้ชื่อบริษัทภาษาอังกฤษตามที่จดทะเบียนไว้เท่านั้น",
      "ตรวจวันที่คัดเอกสารให้อยู่ในกรอบเวลาที่ผู้รับยอมรับ",
      "แปลวัตถุประสงค์และเงื่อนไขการลงนามผูกพันครบทุกข้อ",
      "แนบหมายเหตุประกอบงบการเงินเสมอ",
      "ตรวจว่าผู้ลงนามมีอำนาจตามหนังสือรับรองฉบับล่าสุด",
    ],
    checklistEn: [
      "Use only the registered English company name",
      "Keep the extraction date within the recipient's acceptance window",
      "Translate the objectives and binding-signature conditions in full",
      "Always include the notes to the financial statements",
      "Confirm the signatory's authority against the latest affidavit",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองบริษัทคัดไว้นานแล้วใช้ได้หรือไม่",
        a: "ในทางปฏิบัติผู้รับเอกสาร เช่น ธนาคารหรือนายทะเบียนต่างประเทศ มักกำหนดให้เป็นฉบับที่คัดใหม่ เนื่องจากหนังสือรับรองแสดงสถานะ ณ วันที่คัด ควรสอบถามกรอบเวลาที่ผู้รับยอมรับก่อนเริ่มขั้นตอน",
      },
      {
        q: "ชื่อบริษัทควรแปลหรือทับศัพท์",
        a: "หากบริษัทจดชื่อภาษาอังกฤษไว้กับนายทะเบียนแล้ว ต้องใช้ชื่อนั้นตรงตัว ไม่แปลใหม่ เพราะชื่อที่ไม่ตรงกันทำให้ผู้รับไม่สามารถเชื่อมโยงกับเอกสารจดทะเบียนอื่นได้",
      },
      {
        q: "มติที่ประชุมที่บริษัทพิมพ์เองต้องทำอย่างไร",
        a: "เป็นเอกสารที่เอกชนจัดทำเอง จึงต้องให้ผู้มีอำนาจลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อที่ขึ้นทะเบียนกับสภาทนายความก่อน แล้วจึงนำไปรับรองนิติกรณ์ต่อได้",
      },
      {
        q: "งบการเงินต้องแปลทั้งเล่มหรือไม่",
        a: "ควรแปลทั้งชุดรวมหมายเหตุประกอบงบการเงินและรายงานผู้สอบบัญชี เพราะเป็นส่วนที่อธิบายนโยบายบัญชีและภาระผูกพัน หากผู้รับระบุขอบเขตที่ต้องการชัดเจน ให้ระบุขอบเขตนั้นไว้ในคำรับรอง",
      },
      {
        q: "สัญญาสองภาษาควรระบุอะไรไว้",
        a: "ควรมีข้อกำหนดระบุว่าฉบับภาษาใดมีผลบังคับเมื่อข้อความขัดกัน (governing language) เพื่อลดข้อพิพาทจากความต่างของถ้อยคำระหว่างสองฉบับ",
      },
      {
        q: "เอกสารบริษัทต่างประเทศจะใช้ในไทยต้องทำอย่างไร",
        a: "โดยทั่วไปต้องได้รับการรับรองในประเทศต้นทางตามระบบของประเทศนั้น และรับรองโดยสถานเอกอัครราชทูตไทยหรือสถานกงสุลใหญ่ไทยในประเทศนั้น แล้วจึงแปลเป็นภาษาไทยเพื่อยื่นต่อหน่วยงานในไทย",
      },
    ],
    faqsEn: [
      {
        q: "Can an older company affidavit still be used?",
        a: "Recipients such as banks and foreign registrars usually require a freshly extracted affidavit, because it reflects status as at the extraction date. Ask the recipient what window it accepts before starting.",
      },
      {
        q: "Should the company name be translated or transliterated?",
        a: "If an English name is registered with the registrar, use it verbatim. A different rendering prevents the recipient from linking the document to other registration records.",
      },
      {
        q: "How are company-drafted resolutions handled?",
        a: "Company drafted resolutions are handled as private documents requiring the authorised signatory to sign before a Notarial Services Attorney registered with the Lawyers Council. After this signature process is complete, the file can proceed to official legalisation.",
      },
      {
        q: "Do financial statements need translating in full?",
        a: "Translate the complete set including the notes and the auditor's report, since these explain accounting policies and obligations. If the recipient defines a narrower scope, state that scope in the certificate.",
      },
      {
        q: "What should a bilingual contract specify?",
        a: "A governing-language clause stating which version prevails in case of conflict, to reduce disputes arising from wording differences between the two versions.",
      },
      {
        q: "How are foreign company documents used in Thailand?",
        a: "Foreign company documents used in Thailand are normally certified in their country of origin under that country's system and by the Royal Thai Embassy or Consulate-General there. Afterwards, these documents are translated into Thai for filing with Thai authorities.",
      },
    ],
  },
  {
    slug: "immigration-visa",
    titleTh: "เอกสารตรวจคนเข้าเมืองและวีซ่า",
    titleEn: "Immigration and visa documents",
    summaryTh:
      "หนังสือรับรองความประพฤติ เอกสารการเงิน หนังสือรับรองการทำงาน หนังสือรับรองความสัมพันธ์ และเอกสารประกอบคำร้องวีซ่า — เอกสารที่ถูกตรวจความสอดคล้องข้ามฉบับ",
    summaryEn:
      "Police clearance certificates, financial evidence, employment letters, relationship evidence and visa supporting documents — checked for consistency across the whole bundle.",
    bodyTh: [
      "ชุดเอกสารวีซ่ามีลักษณะเฉพาะคือ เจ้าหน้าที่ไม่ได้อ่านทีละฉบับ แต่อ่านทั้งชุดพร้อมกันเพื่อดูความสอดคล้อง ชื่อ วันที่ ที่อยู่ ตำแหน่งงาน และตัวเลขทางการเงินที่ปรากฏในเอกสารต่างฉบับต้องสอดคล้องกัน คำแปลจึงต้องใช้คำและการสะกดชุดเดียวกันตลอดทั้งชุด ไม่ใช่แปลแยกฉบับโดยไม่เทียบกัน",
      "หนังสือรับรองความประพฤติออกโดยกองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ ผู้ยื่นควรตรวจสอบขั้นตอนและเอกสารประกอบจากหน่วยงานผู้ออกโดยตรง เพราะเงื่อนไขต่างกันระหว่างผู้ยื่นในไทยกับผู้ยื่นจากต่างประเทศ และสถานทูตหลายแห่งกำหนดอายุเอกสารที่ยอมรับไว้ด้วย",
      "เอกสารการเงิน เช่น หนังสือรับรองยอดเงินฝากและรายการเดินบัญชี ต้องแปลโดยคงสกุลเงิน รูปแบบตัวเลข และวันที่ตามต้นฉบับ ห้ามแปลงสกุลเงินหรือปัดตัวเลขในคำแปล หากสถานทูตต้องการยอดเทียบเป็นสกุลเงินอื่น เป็นหน้าที่ของผู้ยื่นที่จะแนบหลักฐานอัตราแลกเปลี่ยนแยกต่างหาก",
      "ข้อกำหนดของแต่ละสถานทูตแตกต่างกันมากและปรับปรุงบ่อย ทั้งเรื่องภาษาที่ยอมรับ รูปแบบคำรับรอง และความจำเป็นในการรับรองนิติกรณ์ ก่อนเริ่มกระบวนการจึงควรอ่านประกาศล่าสุดจากเว็บไซต์ทางการของสถานทูตหรือหน่วยงานตรวจคนเข้าเมืองของประเทศปลายทางเสมอ",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Visa bundles are assessed as a whole rather than document by document. Names, dates, addresses, job titles and financial figures must agree across every item, so the translation has to use one consistent set of terms and spellings throughout the bundle instead of being produced file by file.",
      "Police clearance certificates are issued by the Special Branch Bureau of the Royal Thai Police. Applicants should confirm the procedure and supporting documents with the issuing authority directly, since requirements differ for applicants inside and outside Thailand, and many embassies also set an acceptance age for the certificate.",
      "Financial evidence such as bank balance certificates and statements is translated keeping the currency, number format and dates as printed. Currency conversion and rounding are never done inside a translation; if an embassy wants an equivalent amount, the applicant supplies separate exchange-rate evidence.",
      "Embassy requirements vary widely and are updated often — accepted languages, certification wording and whether legalisation is needed. Always read the destination embassy's or immigration authority's latest official notice before starting.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "หนังสือรับรองความประพฤติ (Police Clearance Certificate)",
      "หนังสือรับรองยอดเงินฝากและรายการเดินบัญชี",
      "หนังสือรับรองการทำงานและสลิปเงินเดือน",
      "ทะเบียนสมรส สูติบัตร และหลักฐานความสัมพันธ์",
      "หนังสือรับรองการเป็นผู้สนับสนุน (Sponsorship letter)",
      "เอกสารทะเบียนบ้านและหลักฐานที่อยู่อาศัย",
    ],
    documentsEn: [
      "Police clearance certificates",
      "Bank balance certificates and account statements",
      "Employment letters and payslips",
      "Marriage and birth certificates and other relationship evidence",
      "Sponsorship letters",
      "House registration and proof of address",
    ],
    chainTh: [
      "อ่านข้อกำหนดล่าสุดของสถานทูตหรือหน่วยงานตรวจคนเข้าเมืองปลายทาง",
      "รวบรวมเอกสารต้นฉบับให้ครบทั้งชุดก่อนเริ่มแปล",
      "แปลทั้งชุดด้วยคำและการสะกดชุดเดียวกัน",
      "รับรองคำแปลตามระดับที่ผู้รับกำหนด (ผู้แปล / ทนายความ / กรมการกงสุล)",
      "ยื่นพร้อมต้นฉบับตามลำดับที่สถานทูตกำหนด",
    ],
    chainEn: [
      "Read the destination embassy's or immigration authority's current requirements",
      "Assemble the complete original bundle before translation starts",
      "Translate the whole bundle using one consistent set of terms and spellings",
      "Certify at the level the recipient requires (translator / attorney / Department of Consular Affairs)",
      "Submit with the originals in the order the embassy specifies",
    ],
    checklistTh: [
      "ใช้การสะกดชื่อตามหนังสือเดินทางทุกฉบับในชุด",
      "ตรวจตัวเลขการเงินและวันที่ให้ตรงกันข้ามเอกสาร",
      "ห้ามแปลงสกุลเงินหรือปัดเศษในคำแปล",
      "ตรวจอายุเอกสารที่สถานทูตยอมรับก่อนยื่น",
      "เก็บสำเนาทั้งชุดที่ยื่นไว้อ้างอิงกรณีถูกขอเอกสารเพิ่ม",
    ],
    checklistEn: [
      "Use the passport spelling of names in every document in the bundle",
      "Cross-check financial figures and dates for consistency",
      "Never convert currencies or round figures in the translation",
      "Confirm the embassy's accepted document age before filing",
      "Keep a copy of the whole submitted bundle for follow-up requests",
    ],
    faqsTh: [
      {
        q: "สถานทูตทุกแห่งต้องการคำแปลที่รับรองโดยกรมการกงสุลหรือไม่",
        a: "ไม่เสมอไป บางแห่งรับคำแปลที่ผู้แปลรับรอง บางแห่งกำหนดให้ผ่านการรับรองนิติกรณ์ และบางแห่งกำหนดผู้แปลที่อยู่ในรายชื่อที่สถานทูตยอมรับ ต้องตรวจสอบประกาศทางการของสถานทูตนั้นเป็นรายกรณี",
      },
      {
        q: "หนังสือรับรองความประพฤติขอที่ไหน",
        a: "ออกโดยกองบัญชาการตำรวจสันติบาล สำนักงานตำรวจแห่งชาติ ขั้นตอนและเอกสารประกอบต่างกันระหว่างผู้ยื่นในประเทศไทยกับผู้ยื่นจากต่างประเทศ ควรตรวจสอบรายละเอียดล่าสุดจากหน่วยงานผู้ออกโดยตรง",
      },
      {
        q: "รายการเดินบัญชีย้อนหลังต้องแปลทุกหน้าหรือไม่",
        a: "ขึ้นอยู่กับข้อกำหนดของผู้รับ หากกำหนดช่วงเวลาที่ต้องการไว้ ให้แปลครบตามช่วงนั้นทุกหน้าและระบุจำนวนหน้าไว้ในคำรับรอง เพื่อแสดงว่าไม่มีการเลือกเฉพาะบางหน้า",
      },
      {
        q: "แปลผิดเล็กน้อยมีผลต่อผลการพิจารณาหรือไม่",
        a: "ความไม่สอดคล้องของชื่อ วันที่ หรือจำนวนเงินอาจทำให้เจ้าหน้าที่ขอเอกสารเพิ่มหรือพิจารณาช้าลง จึงควรตรวจทานความสอดคล้องข้ามเอกสารทั้งชุดก่อนยื่นเสมอ",
      },
      {
        q: "เอกสารที่ออกเป็นภาษาอังกฤษอยู่แล้วต้องแปลไหม",
        a: "โดยทั่วไปไม่ต้องแปลซ้ำหากเป็นเอกสารทางการที่ออกเป็นภาษาอังกฤษ แต่บางสถานทูตยังกำหนดให้ผ่านการรับรองเพิ่มเติม ควรตรวจสอบเงื่อนไขของสถานทูตนั้นก่อน",
      },
      {
        q: "ระยะเวลาดำเนินการนานเท่าใด",
        a: "ระยะเวลาขึ้นอยู่กับหน่วยงานผู้ออกเอกสาร คิวของกรมการกงสุล และคิวของสถานทูตแต่ละแห่ง ซึ่งอยู่นอกเหนือการควบคุมของผู้ให้บริการแปล จึงควรเผื่อเวลาและตรวจสอบคิวล่าสุดจากหน่วยงานที่เกี่ยวข้อง",
      },
    ],
    faqsEn: [
      {
        q: "Do all embassies require consular-legalised translations?",
        a: "No. Some accept a translator's certification, some require legalisation, and some require a translator from their approved list. Check the specific embassy's official notice case by case.",
      },
      {
        q: "Where is a Thai police clearance certificate issued?",
        a: "By the Special Branch Bureau of the Royal Thai Police. Procedures and supporting documents differ for applicants inside and outside Thailand, so confirm current details with the issuing authority.",
      },
      {
        q: "Must every page of a bank statement be translated?",
        a: "Translating every page of a bank statement depends on the recipient's requirement. If a period is specified, translate every page in that period and state the page count in the certificate to show no pages were selected out.",
      },
      {
        q: "Does a small translation error affect the outcome?",
        a: "Inconsistent names, dates or amounts can lead to further document requests or delay. Cross-check the whole bundle for consistency before filing.",
      },
      {
        q: "Do documents already issued in English need translation?",
        a: "Generally not, if they are official English-language documents, though some embassies still require additional certification. Check that embassy's conditions first.",
      },
      {
        q: "How long does the process take?",
        a: "Timing depends on the issuing authority, the consular queue and each embassy's queue, all outside a translation provider's control. Allow extra time and check current queues with the bodies involved.",
      },
    ],
  },
  {
    slug: "medical-insurance",
    titleTh: "เอกสารการแพทย์และประกัน",
    titleEn: "Medical and insurance documents",
    summaryTh:
      "ใบรับรองแพทย์ ประวัติการรักษา ผลตรวจ ใบเคลมประกัน และเอกสารส่งต่อผู้ป่วย — งานแปลที่ต้องคงศัพท์การแพทย์และหน่วยวัดตามต้นฉบับอย่างเคร่งครัด",
    summaryEn:
      "Medical certificates, treatment records, test results, insurance claims and referral letters — where clinical terminology and units must be preserved exactly.",
    bodyTh: [
      "เอกสารการแพทย์เป็นข้อมูลอ่อนไหวและเป็นเอกสารที่มีผลต่อการตัดสินใจทางคลินิกและทางการเงิน คำแปลจึงต้องคงชื่อโรค ชื่อยา ขนาดยา หน่วยวัด และค่าอ้างอิงของห้องปฏิบัติการตามต้นฉบับทุกจุด ห้ามแปลงหน่วยหรือปรับค่าให้เข้ากับมาตรฐานของประเทศปลายทาง เพราะการแปลงหน่วยเป็นการตีความทางคลินิกที่อยู่นอกขอบเขตของงานแปล",
      "ชื่อยาควรคงชื่อสามัญทางยา (generic name) ที่ปรากฏในต้นฉบับ และหากต้นฉบับระบุชื่อการค้า ให้คงไว้พร้อมระบุชื่อสามัญในวงเล็บเมื่อระบุได้จากเอกสารเอง ผู้แปลไม่ควรเพิ่มข้อมูลยาที่ไม่ปรากฏในต้นฉบับ เพราะเอกสารการแพทย์จะถูกใช้ประกอบการรักษาต่อเนื่องหรือการพิจารณาสินไหม",
      "ใบรับรองแพทย์ที่จะใช้ต่างประเทศมักต้องมีชื่อและเลขที่ใบอนุญาตประกอบวิชาชีพเวชกรรมของแพทย์ผู้ออก พร้อมตราประทับของสถานพยาบาล เพื่อให้หน่วยงานปลายทางตรวจสอบย้อนกลับได้ หากเอกสารไม่มีข้อมูลนี้ อาจไม่ผ่านขั้นตอนรับรองในภายหลัง ควรตรวจกับสถานพยาบาลผู้ออกก่อนนำไปแปล",
      "งานเคลมประกันข้ามประเทศมักต้องใช้ทั้งใบเสร็จ ใบสรุปค่ารักษา และเวชระเบียนประกอบกัน คำแปลควรจัดชุดให้ตรงกันทั้งวันที่รับบริการ รหัสรายการ และยอดเงิน เพื่อให้ผู้พิจารณาสินไหมเทียบเอกสารได้โดยไม่ต้องขอข้อมูลเพิ่ม",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Medical records are sensitive documents that drive clinical and financial decisions. Translations must preserve diagnoses, drug names, dosages, units and laboratory reference ranges exactly as printed. Converting units or adjusting values to the destination country's convention is clinical interpretation and falls outside translation.",
      "Drug names keep the generic name shown in the original; where a brand name appears, it is retained, with the generic name in brackets only when the document itself identifies it. Translators do not add medication information absent from the source, because these records support ongoing treatment and claim assessment.",
      "Medical certificates for overseas use normally need the issuing physician's name and medical licence number plus the facility's seal so the receiving authority can verify them. Without these, later certification steps may fail — confirm with the issuing facility before translation.",
      "Cross-border insurance claims usually combine receipts, billing summaries and clinical records. The translated set should align service dates, item codes and amounts so the assessor can reconcile documents without requesting more.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "ใบรับรองแพทย์และใบรับรองความเห็นแพทย์",
      "เวชระเบียนและสรุปประวัติการรักษา",
      "ผลตรวจทางห้องปฏิบัติการและผลภาพวินิจฉัย",
      "ใบเสร็จค่ารักษาและใบสรุปค่าใช้จ่าย",
      "เอกสารเคลมประกันสุขภาพและประกันเดินทาง",
      "หนังสือส่งต่อผู้ป่วยและใบรับรองความพร้อมในการเดินทาง",
    ],
    documentsEn: [
      "Medical certificates and physician opinion letters",
      "Clinical records and treatment summaries",
      "Laboratory results and diagnostic imaging reports",
      "Treatment receipts and billing summaries",
      "Health and travel insurance claim documents",
      "Referral letters and fitness-to-fly certificates",
    ],
    chainTh: [
      "ขอเอกสารฉบับทางการจากสถานพยาบาล พร้อมลายมือชื่อแพทย์และตราประทับ",
      "แปลโดยคงศัพท์การแพทย์ หน่วยวัด และค่าอ้างอิงตามต้นฉบับ",
      "รับรองคำแปลตามระดับที่ผู้รับกำหนด",
      "ยื่นรับรองนิติกรณ์และสถานทูต เฉพาะกรณีที่หน่วยงานปลายทางกำหนด",
    ],
    chainEn: [
      "Request official copies from the facility with physician signature and seal",
      "Translate preserving clinical terms, units and reference ranges",
      "Certify at the level the recipient requires",
      "Add consular and embassy legalisation only where the receiving body requires it",
    ],
    checklistTh: [
      "ตรวจว่ามีชื่อแพทย์และเลขที่ใบอนุญาตประกอบวิชาชีพเวชกรรม",
      "คงหน่วยวัดและค่าอ้างอิงตามต้นฉบับ ห้ามแปลงหน่วย",
      "ตรวจวันที่รับบริการให้ตรงกันทุกฉบับในชุดเคลม",
      "ปกปิดข้อมูลของผู้ป่วยรายอื่นที่อาจติดมาในเอกสาร",
      "ยืนยันกับผู้รับว่าต้องการเวชระเบียนช่วงเวลาใดบ้าง",
    ],
    checklistEn: [
      "Check the physician's name and medical licence number appear",
      "Keep units and reference ranges as printed; never convert",
      "Align service dates across every document in the claim set",
      "Redact any third-party patient data captured in the copies",
      "Confirm with the recipient which period of records is needed",
    ],
    faqsTh: [
      {
        q: "ผู้แปลแปลงหน่วยผลแล็บให้ได้หรือไม่",
        a: "ไม่ควร การแปลงหน่วยหรือปรับค่าเป็นการตีความทางคลินิก ผู้แปลต้องคงค่าและหน่วยตามต้นฉบับ หากผู้รับต้องการค่าที่แปลงแล้ว ควรให้บุคลากรทางการแพทย์เป็นผู้จัดทำแยกต่างหาก",
      },
      {
        q: "ใบรับรองแพทย์ต้องมีข้อมูลอะไรจึงจะใช้ต่างประเทศได้",
        a: "โดยทั่วไปควรมีชื่อผู้ป่วยตรงกับหนังสือเดินทาง วันที่ตรวจ ความเห็นแพทย์ ชื่อและเลขที่ใบอนุญาตประกอบวิชาชีพเวชกรรมของแพทย์ และตราประทับสถานพยาบาล ทั้งนี้ข้อกำหนดสุดท้ายขึ้นอยู่กับหน่วยงานปลายทาง",
      },
      {
        q: "เวชระเบียนขอจากโรงพยาบาลได้อย่างไร",
        a: "ต้องยื่นคำขอตามระเบียบของสถานพยาบาลนั้น โดยผู้ป่วยหรือผู้รับมอบอำนาจ เนื่องจากเป็นข้อมูลส่วนบุคคลที่ได้รับความคุ้มครอง ควรสอบถามแผนกเวชระเบียนของโรงพยาบาลโดยตรงถึงแบบฟอร์มและหลักฐานที่ต้องใช้",
      },
      {
        q: "เอกสารเคลมประกันต้องรับรองระดับใด",
        a: "ขึ้นอยู่กับเงื่อนไขกรมธรรม์และผู้รับประกันภัย บางรายรับคำแปลที่ผู้แปลรับรอง บางรายกำหนดการรับรองเพิ่มเติม ควรขอเงื่อนไขเป็นลายลักษณ์อักษรจากผู้รับประกันภัยก่อนดำเนินการ",
      },
      {
        q: "ข้อมูลผู้ป่วยจะถูกเก็บรักษาอย่างไร",
        a: "เอกสารการแพทย์เป็นข้อมูลส่วนบุคคลอ่อนไหวตามกฎหมายคุ้มครองข้อมูลส่วนบุคคล ควรตกลงขอบเขตการเก็บ การเข้าถึง และระยะเวลาทำลายเอกสารกับผู้ให้บริการแปลไว้ล่วงหน้าเป็นลายลักษณ์อักษร",
      },
      {
        q: "เอกสารการแพทย์ต้องผ่านกรมการกงสุลเสมอหรือไม่",
        a: "ไม่เสมอไป ขึ้นอยู่กับหน่วยงานปลายทาง หลายกรณีใช้คำแปลที่รับรองโดยผู้แปลก็เพียงพอ แต่กรณีที่ใช้ประกอบคำร้องทางราชการหรือคดี อาจต้องผ่านการรับรองนิติกรณ์ด้วย",
      },
    ],
    faqsEn: [
      {
        q: "May the translator convert laboratory units?",
        a: "No. Converting units or adjusting values is clinical interpretation. Values and units stay as printed; if the recipient needs converted figures, a clinician should produce them separately.",
      },
      {
        q: "What must a medical certificate contain for overseas use?",
        a: "Typically the patient's name as in the passport, examination date, the physician's opinion, the physician's name and medical licence number, and the facility's seal. Final requirements are set by the receiving authority.",
      },
      {
        q: "How are hospital records obtained?",
        a: "Through the hospital's own request procedure, by the patient or an authorised representative, because the records are protected personal data. Ask the medical records department which form and evidence are required.",
      },
      {
        q: "What certification level do insurance claims need?",
        a: "The certification level required for insurance claims depends on the policy and the insurer. Some accept a translator's certification; others require more. Always obtain the insurer's requirement in writing before proceeding with the certification process.",
      },
      {
        q: "How is patient data protected?",
        a: "Medical records are sensitive personal data under data protection law. Agree in writing with the translation provider on storage scope, access and destruction timelines in advance.",
      },
      {
        q: "Do medical documents always need consular legalisation?",
        a: "Not always — it depends on the receiving authority. A translator's certification is often enough, but filings with government bodies or courts may require legalisation.",
      },
    ],
  },
];

export function getSwornDocGroup(slug: string): SwornDocGroup | undefined {
  return SWORN_DOC_GROUPS.find((g) => g.slug === slug);
}
